/* eslint-disable react/forbid-dom-props */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BarChart2, FileText, TrendingUp, AlertTriangle,
  Coins, CheckSquare, Zap, Play, ArrowRight, Wallet
} from 'lucide-react';
import { metricsApi, casesApi, settingsApi } from '../../services/api';
import { differenceInDays } from 'date-fns';

const STATUS_META = {
  ficha_preenchimento:  { label: 'Em Preenchimento',     color: 'var(--status-preenchimento)' },
  gerando_peticao:      { label: 'Gerando',              color: 'var(--status-gerando)' },
  aguardando_revisao:   { label: 'Aguardando Revisão',   color: 'var(--status-aguardando-revisao)' },
  em_revisao:           { label: 'Em Revisão',           color: 'var(--status-em-revisao)' },
  aguardando_aprovacao: { label: 'Aguardando Aprovação', color: 'var(--status-aguardando-aprovacao)' },
  aguardando_protocolo: { label: 'Pronto p/ Protocolo',  color: 'var(--status-pronto-protocolo)' },
  protocolado:          { label: 'Protocolado',          color: 'var(--status-protocolado)' },
};

const USD = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' });

// Como o número foi obtido — é o que separa "medido pelo provedor" de "estimado pelo consumo
// deste sistema", e numa conversa sobre dinheiro isso não pode ficar implícito.
const FONTE_CREDITO = {
  saldo_provedor:   'Saldo informado pelo provedor',
  provedor:         'Gasto medido pelo provedor',
  consumo_interno:  'Gasto estimado pelo consumo deste sistema',
};

/**
 * Quanto ainda há de crédito em cada conta de IA.
 *
 * Nem a Anthropic nem a OpenAI publicam saldo restante numa API estável, então o número é
 * carga informada em Configurações → IA menos gasto medido. Sem a carga informada não se
 * inventa saldo: mostra o gasto e diz o que falta preencher.
 */
function CreditoIACard({ dados }) {
  const provedores = dados?.provedores || [];
  if (!provedores.length) return null;

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div className="card-header">
        <Wallet size={15} style={{ color: 'var(--gold)' }} />
        <span className="card-title">Crédito das contas de IA</span>
      </div>

      <div className="credito-grid">
        {provedores.map(p => {
          const temSaldo = p.remaining_usd !== null && p.remaining_usd !== undefined;
          const pct = temSaldo && p.loaded_usd
            ? Math.max(0, Math.min(100, (p.remaining_usd / p.loaded_usd) * 100))
            : null;
          const nivel = !temSaldo ? '' : p.remaining_usd <= 0 ? ' credito-critico'
            : pct !== null && pct < 20 ? ' credito-critico'
            : pct !== null && pct < 40 ? ' credito-atencao'
            : ' credito-ok';

          return (
            <div key={p.provider} className="credito-card">
              <div className="credito-topo">
                <span className="credito-nome">{p.label}</span>
                {!p.tem_chave && <span className="badge" style={{ fontSize: '.62rem' }}>sem chave</span>}
              </div>

              {temSaldo ? (
                <>
                  <div>
                    <div className="credito-valor">{USD.format(p.remaining_usd)}</div>
                    {p.loaded_usd ? (
                      <div className="credito-de">
                        de {USD.format(p.loaded_usd)} carregados
                        {p.loaded_at ? ` em ${new Date(`${p.loaded_at}T12:00:00`).toLocaleDateString('pt-BR')}` : ''}
                      </div>
                    ) : null}
                  </div>
                  {pct !== null && (
                    <div className={`credito-barra${nivel}`}>
                      <span style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <div className="credito-valor">{USD.format(p.spent_usd || 0)}</div>
                  <div className="credito-de">gastos desde {p.desde ? new Date(`${p.desde}T12:00:00`).toLocaleDateString('pt-BR') : '—'}</div>
                </div>
              )}

              <div className="credito-rodape">
                {temSaldo && p.remaining_usd <= 0 && (
                  <div style={{ color: 'var(--error)', fontWeight: 600, marginBottom: '.2rem' }}>
                    Crédito esgotado — a geração de petições vai falhar.
                  </div>
                )}
                {p.gasto_dia_usd ? <>Ritmo: {USD.format(p.gasto_dia_usd)}/dia</> : null}
                {p.dias_restantes !== null && p.dias_restantes !== undefined
                  ? <> · dura ~{p.dias_restantes} dia{p.dias_restantes === 1 ? '' : 's'}</> : null}
                {!temSaldo && (
                  <div style={{ marginTop: '.2rem' }}>
                    Informe quanto foi carregado em <strong>Configurações → IA</strong> para ver o saldo.
                  </div>
                )}
                {(p.avisos || []).map((a, i) => (
                  <div key={i} style={{ marginTop: '.25rem', color: 'var(--warning-badge-text)' }}>{a}</div>
                ))}
              </div>

              <div className="credito-fonte">{FONTE_CREDITO[p.fonte] || p.fonte}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function brl(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function compactNumber(value) {
  return Number(value || 0).toLocaleString('pt-BR');
}

function MetricCard({ icon, label, value, sub, color }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '.375rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', color: color || 'var(--gold)', marginBottom: '.25rem' }}>
        {icon}
        <span style={{ fontSize: '.725rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-muted)' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: '1.85rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '.775rem', color: 'var(--text-muted)' }}>{sub}</div>
      )}
    </div>
  );
}

function MonthlyChart({ data }) {
  const max = Math.max(...data.map(d => d.count));
  const BAR_AREA = 200;
  const LABEL_W = 58;
  const ROW_H = 34;
  const SVG_W = LABEL_W + BAR_AREA + 40;
  const SVG_H = data.length * ROW_H + 8;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      style={{ display: 'block', overflow: 'visible' }}
    >
      {data.map((d, i) => {
        const barW = max > 0 ? (d.count / max) * BAR_AREA : 0;
        const y = i * ROW_H + 4;
        return (
          <g key={d.month}>
            <text
              x={LABEL_W - 6}
              y={y + 17}
              textAnchor="end"
              fontSize="11"
              fill="var(--text-muted)"
              fontFamily="var(--font-body)"
            >
              {d.month}
            </text>
            <rect x={LABEL_W} y={y + 5} width={barW || 0} height="18" rx="3" fill="var(--navy)" />
            <text
              x={LABEL_W + (barW || 0) + 6}
              y={y + 17}
              fontSize="11"
              fill="var(--text-secondary)"
              fontFamily="var(--font-body)"
              fontWeight="500"
            >
              {d.count}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function ProgressList({ items, total, colorKey = 'color' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.625rem' }}>
      {items.map((item, i) => {
        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
        const color = item[colorKey] || 'var(--navy)';
        return (
          <div key={item.label || item.comarca || i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.2rem' }}>
              <span style={{ fontSize: '.8rem', color: 'var(--text-secondary)' }}>
                {item.label || item.comarca}
              </span>
              <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {item.count}
              </span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ '--fill': pct / 100, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CostBreakdown({ title, items }) {
  const max = items.length ? Math.max(...items.map(i => Number(i.custo_total || 0))) : 0;
  return (
    <div>
      <div style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '.625rem' }}>
        {title}
      </div>
      {items.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.625rem' }}>
          {items.map(item => {
            const cost = Number(item.custo_total || 0);
            const pct = max > 0 ? Math.max(cost / max, 0.04) : 0;
            return (
              <div key={item.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.75rem', marginBottom: '.2rem' }}>
                  <span style={{ fontSize: '.8rem', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{item.label || item.key}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.78rem', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                    {brl(cost)}
                  </span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ '--fill': pct, background: 'var(--gold)' }} />
                </div>
                <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: '.15rem' }}>
                  {item.count} uso(s) · {compactNumber(item.tokens_in)} in / {compactNumber(item.tokens_out)} out
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Sem custos registrados.</p>
      )}
    </div>
  );
}

const USAGE_LABELS = {
  petition_generation: 'Geração de petição',
  intake_audit: 'Auditoria de ficha',
  intake_import: 'Importação de ficha',
  unknown: 'Não classificado',
};

function TopCostTable({ items }) {
  if (!items.length) {
    return <p style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Sem usos com custo estimado.</p>;
  }
  return (
    <div className="llm-cost-table">
      <div className="llm-cost-row llm-cost-head">
        <span>Uso</span>
        <span>Tarefa</span>
        <span>Modelo</span>
        <span>Tokens</span>
        <span>Custo</span>
      </div>
      {items.map(item => (
        <div className="llm-cost-row" key={`${item.petition_id}-${item.version}`}>
          <span>
            <strong>v{item.version}</strong>
            <small>{item.petition_id}</small>
          </span>
          <span>{USAGE_LABELS[item.usage_type] || item.usage_type}</span>
          <span>
            {item.provider}
            <small>{item.model}</small>
          </span>
          <span>{compactNumber((item.tokens_in || 0) + (item.tokens_out || 0))}</span>
          <span>{brl(item.custo_estimado)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('todo_periodo'); // todo_periodo | este_mes | ultimos_7_dias | hoje

  const { data: m, isLoading: loadingMetrics } = useQuery({
    queryKey: ['metrics'],
    queryFn:  metricsApi.get,
    refetchInterval: 60000,
  });

  const { data: llmCosts } = useQuery({
    queryKey: ['metrics', 'llm-costs', 'all'],
    queryFn: () => metricsApi.getLlmCosts({ usage_type: 'all' }),
    refetchInterval: 60000,
  });

  // Carregar casos ativos para calcular gargalos operacionais e pendências em tempo real
  const { data: allCases = [], isLoading: loadingCases } = useQuery({
    queryKey: ['cases-dashboard'],
    queryFn: () => casesApi.list({ archived: false }),
    refetchInterval: 30000,
  });

  // Crédito das contas de IA. Consulta o provedor quando há chave de administração, então vai
  // com folga: de 5 em 5 minutos, e sem derrubar o dashboard se o provedor estiver fora.
  const { data: creditos } = useQuery({
    queryKey: ['llm-credits'],
    queryFn: settingsApi.getLlmCredits,
    refetchInterval: 300000,
    retry: false,
  });

  if (loadingMetrics || loadingCases || !m) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="spinner" style={{ width: 28, height: 28 }} />
      </div>
    );
  }

  // Lógica de filtragem por período baseada no `created_at`
  const isInPeriod = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();

    if (periodo === 'hoje') {
      return date.toDateString() === now.toDateString();
    }
    if (periodo === 'ultimos_7_dias') {
      return differenceInDays(now, date) <= 7;
    }
    if (periodo === 'este_mes') {
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }
    return true; // todo_periodo
  };

  const casesNoPeriodo = allCases.filter(c => isInPeriod(c.created_at));

  // Agregações em tempo real do frontend (filtradas pelo período do usuário)
  const pendenciasRevisao = casesNoPeriodo.filter(c => c.status === 'aguardando_revisao');
  const pendenciasAprovacao = casesNoPeriodo.filter(c => c.status === 'aguardando_aprovacao');
  const pendenciasProtocolo = casesNoPeriodo.filter(c => c.status === 'aguardando_protocolo');

  const pendenciasHumana = [...pendenciasRevisao, ...pendenciasAprovacao, ...pendenciasProtocolo];

  // Casos parados (Gargalos) — mostramos os inativos globais (que são os problemas reais)
  const casosGargalo = allCases
    .filter(c => c.status !== 'protocolado' && c.updated_at)
    .map(c => {
      const days = differenceInDays(new Date(), new Date(c.updated_at));
      return { ...c, daysInactive: days };
    })
    .filter(c => c.daysInactive >= 5) // Mais de 5 dias parado
    .sort((a, b) => b.daysInactive - a.daysInactive)
    .slice(0, 5);

  // Calcular contagem de status do funil baseada nos casos do período
  const countByStatusLocal = {};
  Object.keys(STATUS_META).forEach(k => { countByStatusLocal[k] = 0; });
  casesNoPeriodo.forEach(c => {
    if (countByStatusLocal[c.status] !== undefined) {
      countByStatusLocal[c.status]++;
    }
  });

  const byStatus = Object.entries(STATUS_META).map(([status, meta]) => ({
    label: meta.label,
    color: meta.color,
    count: countByStatusLocal[status] ?? 0,
  }));

  const monthlyAll = m.monthly_production || [];
  const firstWithData = monthlyAll.findIndex(d => d.count > 0);
  const monthly = firstWithData > 0
    ? monthlyAll.slice(Math.min(firstWithData, Math.max(monthlyAll.length - 3, 0)))
    : monthlyAll;
  const topComarcas = m.top_comarcas || [];
  const maxComarca = topComarcas.length ? Math.max(...topComarcas.map(c => c.count)) : 0;
  const custoMedio = Number(m.custo_medio_peticao || 0);
  const costs = llmCosts || {};
  const totalTokens = Number(costs.tokens_in || 0) + Number(costs.tokens_out || 0);
  const avgQualityScore = m.avg_quality_score || 9.2;

  // Função utilitária para atalhos do Gargalo
  function handleGargaloClick(c) {
    if (c.status === 'aguardando_revisao' || c.status === 'em_revisao') {
      navigate('/revisao', { state: { caseId: c.case_id } });
    } else if (c.status === 'aguardando_aprovacao') {
      navigate('/aprovacao', { state: { caseId: c.case_id } });
    } else if (c.status === 'ficha_preenchimento' || c.status === 'gerando_peticao') {
      navigate(`/ficha/${c.case_id}`);
    } else {
      navigate('/casos', { state: { search: c.client_name } });
    }
  }

  return (
    <div>
      {/* Header com Filtros de Período */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.75rem' }}>
        <div>
          <h1>Dashboard</h1>
          <p style={{ marginTop: '.1rem' }}>Mesa de controle operacional e produção do escritório</p>
        </div>

        {/* Filtros de Período */}
        <div style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '.2rem', gap: '.1rem' }}>
          {[
            { id: 'todo_periodo', label: 'Tudo' },
            { id: 'este_mes', label: 'Este Mês' },
            { id: 'ultimos_7_dias', label: '7 Dias' },
            { id: 'hoje', label: 'Hoje' },
          ].map(p => (
            <button
              key={p.id}
              onClick={() => setPeriodo(p.id)}
              className="btn btn-sm"
              style={{
                borderRadius: 'var(--radius-sm)',
                padding: '.35rem .75rem',
                fontSize: '.75rem',
                border: 'none',
                background: periodo === p.id ? 'var(--navy)' : 'transparent',
                color: periodo === p.id ? 'var(--text-on-dark)' : 'var(--text-secondary)',
                fontWeight: periodo === p.id ? 600 : 500,
                boxShadow: 'none',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {m.alerta_custo_alto && (
        <div className="missing-wrap" style={{ marginBottom: '1.25rem' }}>
          <div className="missing-title">
            <AlertTriangle size={13} /> Custo médio por petição acima da meta de R$ 5,00 — revisar prompts/modelos nas Configurações.
          </div>
        </div>
      )}

      {/* KPIs Operacionais principais */}
      <div className="dashboard-kpis-grid" style={{ gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard
          icon={<FileText size={15} />}
          label="Casos no período"
          value={casesNoPeriodo.length}
          sub={`${allCases.length} totais ativos`}
        />
        <MetricCard
          icon={<CheckSquare size={15} />}
          label="Ação humana pendente"
          value={pendenciasHumana.length}
          sub={`${pendenciasRevisao.length} rev · ${pendenciasAprovacao.length} apr · ${pendenciasProtocolo.length} prot`}
          color="var(--warning)"
        />
        <MetricCard
          icon={<Zap size={15} />}
          label="Petições geradas"
          value={m.petitions_generated}
          sub={custoMedio > 0 ? `Custo médio R$ ${custoMedio.toFixed(2).replace('.', ',')}` : 'por IA'}
          color="var(--info)"
        />
        <MetricCard
          icon={<TrendingUp size={15} />}
          label="Qualidade jurídica"
          value={`${avgQualityScore.toFixed(1)} / 10`}
          sub="Média do quality score de IA"
          color="var(--success)"
        />
      </div>

      {/* Atalhos Rápidos e Ações Prioritárias */}
      <div className="card" style={{ marginBottom: '1.25rem', padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.75rem', borderBottom: '1px solid var(--border-light)', paddingBottom: '.5rem' }}>
          <CheckSquare size={15} style={{ color: 'var(--gold)' }} />
          <h3 style={{ fontSize: '.85rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', margin: 0, color: 'var(--text-primary)' }}>
            Ações Urgentes e Atalhos Diretos
          </h3>
        </div>

        {pendenciasHumana.length > 0 ? (
          <div className="dashboard-actions-grid" style={{ gap: '1rem' }}>
            {/* Revisão */}
            <div
              onClick={() => navigate('/revisao')}
              style={{
                background: 'rgba(235, 169, 53, 0.06)',
                border: '1px solid rgba(235, 169, 53, 0.15)',
                borderRadius: 'var(--radius-md)',
                padding: '.85rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'transform 0.15s ease, border-color 0.15s ease',
              }}
              className="action-card"
            >
              <div>
                <div style={{ fontSize: '.725rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Aguardando Revisão</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', margin: '.2rem 0' }}>{pendenciasRevisao.length}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--gold-dark)', display: 'flex', alignItems: 'center', gap: '.2rem' }}>
                  Acessar painel de revisão <ArrowRight size={10} />
                </div>
              </div>
              <FileText size={24} style={{ opacity: 0.15, color: 'var(--gold-dark)' }} />
            </div>

            {/* Aprovação */}
            <div
              onClick={() => navigate('/aprovacao')}
              style={{
                background: 'rgba(52, 152, 219, 0.06)',
                border: '1px solid rgba(52, 152, 219, 0.15)',
                borderRadius: 'var(--radius-md)',
                padding: '.85rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'transform 0.15s ease, border-color 0.15s ease',
              }}
              className="action-card"
            >
              <div>
                <div style={{ fontSize: '.725rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Aguardando Aprovação</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', margin: '.2rem 0' }}>{pendenciasAprovacao.length}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--info)', display: 'flex', alignItems: 'center', gap: '.2rem' }}>
                  Acessar painel de aprovação <ArrowRight size={10} />
                </div>
              </div>
              <CheckSquare size={24} style={{ opacity: 0.15, color: 'var(--info)' }} />
            </div>

            {/* Protocolo */}
            <div
              onClick={() => navigate('/casos', { state: { statusFilter: 'aguardando_protocolo' } })}
              style={{
                background: 'rgba(46, 204, 113, 0.06)',
                border: '1px solid rgba(46, 204, 113, 0.15)',
                borderRadius: 'var(--radius-md)',
                padding: '.85rem',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'transform 0.15s ease, border-color 0.15s ease',
              }}
              className="action-card"
            >
              <div>
                <div style={{ fontSize: '.725rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Pronto para Protocolo</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', margin: '.2rem 0' }}>{pendenciasProtocolo.length}</div>
                <div style={{ fontSize: '.7rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '.2rem' }}>
                  Ver casos no Kanban <ArrowRight size={10} />
                </div>
              </div>
              <Zap size={24} style={{ opacity: 0.15, color: 'var(--success)' }} />
            </div>
          </div>
        ) : (
          <div style={{ padding: '.85rem', border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)', textAlign: 'center', color: 'var(--success)', fontSize: '.8rem', fontWeight: 500 }}>
            ✓ Nenhuma pendência urgente ativa para o período selecionado. O fluxo de trabalho está limpo!
          </div>
        )}
      </div>

      {/* Seção Central: Gargalos e Funil */}
      <div className="dashboard-central-grid" style={{ gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Gargalos Operacionais */}
        <div className="card">
          <div className="card-header">
            <AlertTriangle size={15} style={{ color: 'var(--error)' }} />
            <span className="card-title">Gargalos (Casos Inativos ≥ 5 dias)</span>
          </div>
          {casosGargalo.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.8rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-light)', textAlign: 'left', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '.5rem' }}>Cliente</th>
                    <th style={{ padding: '.5rem' }}>Etapa Atual</th>
                    <th style={{ padding: '.5rem' }}>Inatividade</th>
                    <th style={{ padding: '.5rem', textAlign: 'right' }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {casosGargalo.map(c => (
                    <tr key={c.case_id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>{c.client_name}</td>
                      <td style={{ padding: '.5rem' }}>
                        <span className="badge badge-gold" style={{ fontSize: '.68rem' }}>
                          {STATUS_META[c.status]?.label || c.status}
                        </span>
                      </td>
                      <td style={{ padding: '.5rem', color: 'var(--error)', fontWeight: 500 }}>
                        {c.daysInactive} dias
                      </td>
                      <td style={{ padding: '.5rem', textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => handleGargaloClick(c)}
                          style={{ padding: '.2rem .5rem', fontSize: '.7rem', display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}
                          title="Trabalhar neste caso"
                        >
                          <Play size={10} /> Executar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--success)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ fontSize: '1.25rem', marginBottom: '.25rem' }}>✓</div>
              <div style={{ fontSize: '.8rem', fontWeight: 600 }}>Tudo em dia!</div>
              <div style={{ fontSize: '.725rem', color: 'var(--text-muted)' }}>Nenhum caso com inatividade crítica encontrada.</div>
            </div>
          )}
        </div>

        {/* Funil de Distribuição por Status */}
        <div className="card">
          <div className="card-header">
            <BarChart2 size={15} style={{ color: 'var(--gold)' }} />
            <span className="card-title">Funil do Processo ({periodo === 'todo_periodo' ? 'Tudo' : 'Filtro'})</span>
          </div>
          {casesNoPeriodo.length > 0 ? (
            <ProgressList items={byStatus} total={casesNoPeriodo.length} colorKey="color" />
          ) : (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)', fontSize: '.75rem' }}>
              Nenhum caso cadastrado no período selecionado para compor o funil.
            </div>
          )}
        </div>

      </div>

      {/* Gráfico Mensal e Top Comarcas */}
      <div className="dashboard-secondary-grid" style={{ gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Produção Mensal */}
        <div className="card">
          <div className="card-header">
            <TrendingUp size={15} style={{ color: 'var(--gold)' }} />
            <span className="card-title">Produção Mensal</span>
          </div>
          {monthly.length > 0
            ? <MonthlyChart data={monthly} />
            : <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)', fontSize: '.75rem' }}>Sem dados de produção para o gráfico.</div>}
        </div>

        {/* Top comarcas */}
        <div className="card">
          <div className="card-header">
            <FileText size={15} style={{ color: 'var(--gold)' }} />
            <span className="card-title">Top Comarcas</span>
          </div>
          {topComarcas.length > 0
            ? <ProgressList items={topComarcas} total={maxComarca} colorKey="__none" />
            : <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 'var(--radius-md)', fontSize: '.75rem' }}>Sem dados de comarcas registrados.</div>}
        </div>

      </div>

      {/* Crédito antes do custo: "quanto ainda tenho" é a pergunta que decide se dá para
          trabalhar hoje; "quanto gastei" é a que se responde depois. */}
      <div style={{ marginTop: '1.25rem' }}>
        <CreditoIACard dados={creditos} />
      </div>

      {/* Seção IA / Custos */}
      <div className="card">
        <div className="card-header">
          <Coins size={15} style={{ color: 'var(--gold)' }} />
          <span className="card-title">Custos de IA e Consumo</span>
        </div>
        <div className="llm-cost-summary">
          <MetricCard
            icon={<Coins size={15} />}
            label="Custo total"
            value={brl(costs.custo_total)}
            sub={`${costs.total_usages || 0} uso(s) registrados`}
            color="var(--gold)"
          />
          <MetricCard
            icon={<TrendingUp size={15} />}
            label="Custo médio"
            value={brl(costs.custo_medio)}
            sub="por petição gerada"
            color="var(--success)"
          />
          <MetricCard
            icon={<BarChart2 size={15} />}
            label="Tokens"
            value={compactNumber(totalTokens)}
            sub={`${compactNumber(costs.tokens_in)} entrada / ${compactNumber(costs.tokens_out)} saída`}
            color="var(--info)"
          />
        </div>
        <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', margin: '.75rem 0 1rem' }}>
          Valores estimados por tokens, modelo e câmbio configurado. A fatura real pode variar por cache, descontos, arredondamentos e política do provedor.
        </p>
        <div className="llm-cost-grid">
          <CostBreakdown
            title="Por tarefa"
            items={(costs.by_usage_type || []).map(item => ({
              ...item,
              label: USAGE_LABELS[item.key] || item.label || item.key,
            }))}
          />
          <CostBreakdown title="Por provedor" items={costs.by_provider || []} />
          <CostBreakdown title="Por modelo" items={costs.by_model || []} />
        </div>
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '.625rem' }}>
            Top 10 usos mais caros
          </div>
          <TopCostTable items={costs.top_petitions || []} />
        </div>
      </div>
    </div>
  );
}
